
import { computed, defineComponent, onBeforeMount, PropType, reactive, toRaw } from 'vue'

// components
import Modal from '@/common/components/UI/Modal/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'

// validation
import useVuelidate from '@vuelidate/core'
import { CreateSLAValidation } from '@/modules/SLAModule/validations/CreateSLA.validation'

// types
import { SlaT } from '@/core/types/sla.types'
import { CityT } from '@/core/types/cities.types'

// constants
import { SlaTariffs, SLA_CARRIERS } from '@/core/constants/sla.constants'

// composable
import useHelpers from '@/common/composable/useHelpers'
import { DataState } from '@/core/classes/DataState.class'

// realisations
import { cities } from '@/core/realisations/cities.realisation'

const PERIOD_OPTIONS = [
    { id: 1, name: 'Летний' },
    { id: 2, name: 'Зимний' },
]

export default defineComponent({
    components: {
        Modal,
        TheInput,
        TheSelect,
        TheButton,
        Checkbox,
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
        isCreate: {
            type: Boolean,
            default: false,
        },
        isUpdate: {
            type: Boolean,
            default: false,
        },
        isDelete: {
            type: Boolean,
            default: false,
        },
        initialValue: {
            type: Object as PropType<SlaT>,
            default: null,
        },
    },
    emits: ['create', 'update', 'delete', 'cancel'],
    setup(props, { emit }) {
        const state = reactive({
            id: null as number | null,
            city_from: null as number | null,
            city_to: null as number | null,
            pickup: null as number | null,
            processing: null as number | null,
            transit: null as number | null,
            delivery: null as number | null,
            tariff: null as string | null,
            supplier_id: null as number | null,
            is_active: true,
            periodId: null as number | null,
            transit_cities: [] as Array<number | null>,
        })

        const data_state = reactive({
            from_cities: new DataState<CityT>({}),
            to_cities: new DataState<CityT>({}),
            transit_cities: new DataState<CityT>({}),
        })

        const state_form$ = useVuelidate(CreateSLAValidation, state)

        const addTransitCity = () => {
            state.transit_cities.push(null)
        }

        const deleteTransitCity = (transit_city_index: number) => {
            const answer = confirm(
                `Вы действительно хотите удалить транзитный город №${transit_city_index + 1}?`
            )
            if (!answer) return

            state.transit_cities.splice(transit_city_index, 1)
        }

        const loadFromCities = async (page: number, search?: string) => {
            if (props.isDelete) return

            data_state.from_cities.pagination.current_page = page

            try {
                data_state.from_cities.is_loading = true

                const data = await cities.getAllList(page, search)
                data_state.from_cities.list = data.data

                data_state.from_cities.pagination.total_pages = data.meta.last_page

                data_state.from_cities.is_failed = false
                data_state.from_cities.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.from_cities.failure.catchError(error)

                if (!data_state.from_cities.failure.error_state.is_cancel) {
                    data_state.from_cities.is_failed = true
                }
            } finally {
                if (!data_state.from_cities.failure.error_state.is_cancel) {
                    data_state.from_cities.is_loading = false
                }
            }
        }

        const loadToCities = async (page: number, search?: string) => {
            if (props.isDelete) return

            data_state.to_cities.pagination.current_page = page

            try {
                data_state.to_cities.is_loading = true

                const data = await cities.getAllList(page, search)
                data_state.to_cities.list = data.data

                data_state.to_cities.pagination.total_pages = data.meta.last_page

                data_state.to_cities.is_failed = false
                data_state.to_cities.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.to_cities.failure.catchError(error)

                if (!data_state.to_cities.failure.error_state.is_cancel) {
                    data_state.to_cities.is_failed = true
                }
            } finally {
                if (!data_state.to_cities.failure.error_state.is_cancel) {
                    data_state.to_cities.is_loading = false
                }
            }
        }

        const loadTransitCities = async (page: number, search?: string) => {
            if (props.isDelete) return

            data_state.transit_cities.pagination.current_page = page

            try {
                data_state.transit_cities.is_loading = true

                const data = await cities.getAllList(page, search)
                data_state.transit_cities.list = data.data

                data_state.transit_cities.pagination.total_pages = data.meta.last_page

                data_state.transit_cities.is_failed = false
                data_state.transit_cities.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.transit_cities.failure.catchError(error)

                if (!data_state.transit_cities.failure.error_state.is_cancel) {
                    data_state.transit_cities.is_failed = true
                }
            } finally {
                if (!data_state.transit_cities.failure.error_state.is_cancel) {
                    data_state.transit_cities.is_loading = false
                }
            }
        }

        const loadMoreFromCities = async (page: number) => {
            if (page > data_state.from_cities.pagination.total_pages) return
            data_state.from_cities.pagination.current_page = page

            try {
                data_state.from_cities.is_loading = true

                const response = await cities.getAllList(page)
                data_state.from_cities.list = data_state.from_cities.list.concat(response.data)

                data_state.from_cities.pagination.total_pages = response.meta.last_page

                data_state.from_cities.is_failed = false
                data_state.from_cities.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.from_cities.failure.catchError(error)

                if (!data_state.from_cities.failure.error_state.is_cancel) {
                    data_state.from_cities.is_failed = true
                }
            } finally {
                if (!data_state.from_cities.failure.error_state.is_cancel) {
                    data_state.from_cities.is_loading = false
                }
            }
        }

        const loadMoreToCities = async (page: number) => {
            if (page > data_state.to_cities.pagination.total_pages) return
            data_state.to_cities.pagination.current_page = page

            try {
                data_state.to_cities.is_loading = true

                const response = await cities.getAllList(page)
                data_state.to_cities.list = data_state.to_cities.list.concat(response.data)

                data_state.to_cities.pagination.total_pages = response.meta.last_page

                data_state.to_cities.is_failed = false
                data_state.to_cities.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.to_cities.failure.catchError(error)

                if (!data_state.to_cities.failure.error_state.is_cancel) {
                    data_state.to_cities.is_failed = true
                }
            } finally {
                if (!data_state.to_cities.failure.error_state.is_cancel) {
                    data_state.to_cities.is_loading = false
                }
            }
        }

        const loadMoreTransitCities = async (page: number) => {
            if (page > data_state.transit_cities.pagination.total_pages) return
            data_state.transit_cities.pagination.current_page = page

            try {
                data_state.transit_cities.is_loading = true

                const response = await cities.getAllList(page)
                data_state.transit_cities.list = data_state.transit_cities.list.concat(
                    response.data
                )

                data_state.transit_cities.pagination.total_pages = response.meta.last_page

                data_state.transit_cities.is_failed = false
                data_state.transit_cities.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.transit_cities.failure.catchError(error)

                if (!data_state.transit_cities.failure.error_state.is_cancel) {
                    data_state.transit_cities.is_failed = true
                }
            } finally {
                if (!data_state.transit_cities.failure.error_state.is_cancel) {
                    data_state.transit_cities.is_loading = false
                }
            }
        }

        const searchFromCity = (search_query: string) => {
            // data_state.from_cities.pagination.current_page
            loadFromCities(1, search_query)
        }

        const searchToCity = (search_query: string) => {
            // data_state.to_cities.pagination.current_page
            loadToCities(1, search_query)
        }

        const searchTransitCity = (search_query: string) => {
            // data_state.to_cities.pagination.current_page
            loadTransitCities(1, search_query)
        }

        const loadCities = async () => {
            try {
                data_state.to_cities.is_loading = true
                data_state.from_cities.is_loading = true

                const data = await cities.getAllList(1)

                data_state.to_cities.list = data.data
                data_state.from_cities.list = data.data
                data_state.transit_cities.list = data.data

                data_state.to_cities.pagination.total_pages = data.meta.last_page
                data_state.from_cities.pagination.total_pages = data.meta.last_page
                data_state.transit_cities.pagination.total_pages = data.meta.last_page

                data_state.to_cities.is_failed = false
                data_state.from_cities.is_failed = false
                data_state.transit_cities.is_failed = false

                data_state.to_cities.failure.error_state.is_cancel = false
                data_state.from_cities.failure.error_state.is_cancel = false
                data_state.transit_cities.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.to_cities.failure.catchError(error)
                data_state.from_cities.failure.catchError(error)
                data_state.transit_cities.failure.catchError(error)

                if (
                    !data_state.to_cities.failure.error_state.is_cancel &&
                    !data_state.from_cities.failure.error_state.is_cancel &&
                    !data_state.transit_cities.failure.error_state.is_cancel
                ) {
                    data_state.to_cities.is_failed = true
                    data_state.from_cities.is_failed = true
                    data_state.transit_cities.is_failed = true
                }
            } finally {
                if (
                    !data_state.to_cities.failure.error_state.is_cancel &&
                    !data_state.from_cities.failure.error_state.is_cancel &&
                    !data_state.transit_cities.failure.error_state.is_cancel
                ) {
                    data_state.to_cities.is_loading = false
                    data_state.from_cities.is_loading = false
                    data_state.transit_cities.is_loading = false
                }
            }
        }

        const unshiftFindedToCity = async (city: CityT) => {
            await loadToCities(data_state.to_cities.pagination.current_page)
            data_state.to_cities.list.unshift(city)
        }

        const unshiftFindedFromCity = async (city: CityT) => {
            await loadFromCities(data_state.from_cities.pagination.current_page)
            data_state.from_cities.list.unshift(city)
        }

        const unshiftFindedTransitCity = async (city: CityT) => {
            await loadTransitCities(data_state.transit_cities.pagination.current_page)
            data_state.transit_cities.list.unshift(city)
        }

        const modalTitle = computed(() => {
            if (props.isCreate) return 'Добавление SLA'
            if (props.isUpdate) return 'Редактирование SLA'
            if (props.isDelete) return 'Удаление SLA'
            return 'modal title'
        })

        const modalConfirmButtonText = computed(() => {
            if (props.isCreate) return 'Добавить'
            if (props.isUpdate) return 'Редактировать'
            if (props.isDelete) return 'Удалить'
            return 'Подтвердить'
        })

        onBeforeMount(async () => {
            if (props.isCreate) {
                loadCities()
                return
            }

            if (!props.initialValue) return

            state.id = props.initialValue.id
            state.periodId = props.initialValue.periodId
            state.pickup = props.initialValue.pickup
            state.processing = props.initialValue.processing
            state.transit = props.initialValue.transit
            state.delivery = props.initialValue.delivery
            state.supplier_id = props.initialValue.supplierId
            state.is_active =
                props.initialValue.isActive === null ? true : props.initialValue.isActive

            if (props.initialValue.transitCities && props.initialValue.transitCities.length) {
                state.transit_cities = props.initialValue.transitCities
            }

            const { findObjectByValue } = useHelpers()
            const tariff = findObjectByValue('name', props.initialValue.tariff, SlaTariffs)
            if (tariff) state.tariff = tariff.api_key

            if (props.isDelete) return

            await loadCities()

            const city_from = findObjectByValue<CityT>(
                'name',
                props.initialValue.fromCity,
                data_state.from_cities.list
            )

            const city_to = findObjectByValue<CityT>(
                'name',
                props.initialValue.toCity,
                data_state.to_cities.list
            )

            if (city_from) state.city_from = city_from.id
            if (city_to) state.city_to = city_to.id

            state_form$.value.$reset()
        })

        /** create emit */
        const emitCreate = () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            emit('create', toRaw(state))
        }

        /** update emit */
        const emitUpdate = () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            emit('update', toRaw(state))
        }

        /** delete emit */
        const emitDelete = () => {
            emit('delete', state.id)
        }

        const emitManager = () => {
            if (props.isCreate) emitCreate()
            if (props.isUpdate) emitUpdate()
            if (props.isDelete) emitDelete()
        }

        const emitCancel = () => {
            emit('cancel')
        }

        const onTariffChange = () => {
            state.is_active = true
            state.supplier_id = null
        }

        //

        const isExpress = computed(() => state.tariff === 'express')

        //

        return {
            state,
            data_state,
            state_form$,
            SlaTariffs,
            modalTitle,
            modalConfirmButtonText,
            emitManager,
            emitCancel,
            loadFromCities,
            loadToCities,
            loadTransitCities,
            loadMoreFromCities,
            loadMoreToCities,
            loadMoreTransitCities,
            searchFromCity,
            searchToCity,
            searchTransitCity,
            unshiftFindedFromCity,
            unshiftFindedToCity,
            unshiftFindedTransitCity,
            onTariffChange,
            addTransitCity,
            deleteTransitCity,
            //
            isExpress,
            //
            SLA_CARRIERS,
            PERIOD_OPTIONS,
        }
    },
})
